'use client';

import React, { useCallback, useMemo, useState } from 'react';

import { useOffers } from '@/api/useOffers';
import { usePages } from '@/api/usePages';
import { TopBarIndicator } from '@/app/appcharge/components/TopBarIndicator';
import { CmsImage } from '@/components/CmsImage';
import { Dialog, DialogOverlay, DialogTrigger } from '@/components/ui/dialog';
import { useLockBody } from '@/hooks/useLockBody';
import { EDesktopNavigationLayout, EPageSection } from '@/lib/enums';
import { preserveQueryParams } from '@/lib/utils';
import { useAppState } from '@/state/hooks/app.state.hook';
import { useLoginState } from '@/state/hooks/login.state.hook';
import { AcCircularLoader } from '@/components/portal-ui';
import { getCustomLoaderAnimation } from '@/lib/loaderAnimation';
import { useCloseOnBackNavigation } from '@web-store-platforms/hooks/use-close-on-back-navigation';

import { CustomButton } from './CustomButton';
import { handleTopbarLoginClick } from './Topbar.utils';
import { LogoutModal } from './Login/Components/LogoutModal';
import MenuItem, { DEFAULT_TEXT_COLOR } from './MenuItem';
import { TopbarMenuItem } from './TopbarMenuItem';
import { MenuIcon, X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { parseCssObject } from './cssObjectToString';
import { StyledMenuScope, useMenuScopeProps } from './menuStyle';
import { CssObject, StyledPortalHeader } from './style';

export const Topbar: React.FC = () => {
  const t = useTranslations();
  const { getOffers } = useOffers({});

  const [isOpen, setIsOpen] = useState(false);
  const [isLogoutOpen, setIsLogoutOpen] = useState(false);

  useLockBody(isOpen);
  useCloseOnBackNavigation(isLogoutOpen, () => setIsLogoutOpen(false));

  const { initialBoot, isGlobalLoading, design, playerData } = useAppState();
  const { setIsLoginModalOpen, isLoggedIn, isOtpLoggingIn } = useLoginState();
  const onLoginButtonClick = useCallback((): void => {
    handleTopbarLoginClick({
      isOtpLoggingIn,
      setIsLoginModalOpen,
    });
  }, [isOtpLoggingIn, setIsLoginModalOpen]);
  const { getPages } = usePages({ portalId: initialBoot?.portalId });
  const menuDesign = useMemo(() => initialBoot?.theme?.menu, [initialBoot?.theme?.menu]);
  const menuScopeProps = useMenuScopeProps();
  const isTopFixedHeader =
    menuDesign?.desktopNavigationLayout === EDesktopNavigationLayout.TOP_FIXED_HEADER;

  const shouldShowLoadingIndicator = useMemo(() => {
    if (isGlobalLoading) {
      return true;
    }

    return Boolean(playerData?.playerId && getPages.isLoading);
  }, [isGlobalLoading, playerData?.playerId, getPages.isLoading]);

  const balances = useMemo(() => {
    if (getOffers.isLoading || !getOffers.data) return [];

    return getOffers.data.balances.map((item: any) => {
      return {
        formattedQuantity: item.formattedQuantity || 0,
        image: item.images?.product || '',
      };
    });
  }, [getOffers.isLoading, getOffers.data]);

  const playerImg = useMemo(
    () => playerData?.profileImageUrl || 'https://media.appcharge.com/defaults/old-profile.png',
    [playerData?.profileImageUrl]
  );

  const playerFrameImg = useMemo(() => {
    return getOffers?.data?.profileFrameImg || '';
  }, [getOffers?.data?.profileFrameImg]);

  const playerLevelImg = useMemo(() => {
    return getOffers?.data?.playerLevel?.assetUrl || '';
  }, [getOffers?.data?.playerLevel?.assetUrl]);

  const playerLevelText = useMemo(() => {
    return getOffers?.data?.playerLevel?.text || '';
  }, [getOffers?.data?.playerLevel?.text]);

  const shouldShowPlayerName = useMemo(() => {
    return initialBoot?.theme?.header?.showPlayerName;
  }, [initialBoot]);

  const shouldShowPlayerId = useMemo(() => {
    return initialBoot?.theme?.header?.showPlayerId;
  }, [initialBoot]);

  const shouldShowBalances = useMemo(() => {
    return initialBoot?.theme?.header?.showPlayerBalance;
  }, [initialBoot]);

  const headerCss: CssObject | undefined = useMemo(
    () => parseCssObject(initialBoot?.theme?.header?.css),
    [initialBoot?.theme?.header?.css]
  );

  const getMenuItemIcon = (pageTemplate: string, icon: string) => {
    return (
      <CmsImage
        data-testid="PortalSidebar-ItemIcon"
        src={icon}
        alt={pageTemplate}
        width={46}
        height={46}
        className="menu-icon my-2.5 size-[46px] shrink-0"
      />
    );
  };

  const pages = useMemo(() => {
    if (playerData?.playerId && initialBoot?.portalId) {
      return getPages.data;
    }

    return initialBoot?.pages;
  }, [getPages.data, initialBoot?.pages, playerData?.playerId, initialBoot?.portalId]);

  const isPagesLoading = useMemo(() => {
    // Don't show sidebar loader when global loading is true (e.g., language change)
    if (isGlobalLoading) {
      return false;
    }

    return playerData?.playerId && getPages.isLoading;
  }, [playerData?.playerId, getPages.isLoading, isGlobalLoading]);

  const groupedPages = useMemo(() => {
    if (!Array.isArray(pages) || pages.length === 0) return [];

    const mainPages: typeof pages = [];
    const subPagesByParent: Record<string, typeof pages> = {};

    for (const page of pages) {
      if (page.isSubPage) {
        const parentId = page.parentPageId!;
        if (!subPagesByParent[parentId]) {
          subPagesByParent[parentId] = [];
        }
        subPagesByParent[parentId].push(page);
      } else {
        mainPages.push(page);
      }
    }

    return mainPages
      .map((page) => ({
        ...page,
        subPages: (subPagesByParent[page.id] || []).sort(
          (a, b) => (a.subPagePriority ?? 0) - (b.subPagePriority ?? 0)
        ),
      }))
      .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
  }, [pages]);

  const getMobileMenuItems = () => {
    return groupedPages.map((page, index) => {
      const handleMenuItemClick = () => {
        setIsOpen(false);
      };

      return (
        <div
          key={page.id || index}
          data-priority={page.priority}
          data-testid={`PortalSidebar-Item-${page.slug}`}
          className="w-full"
          onClick={handleMenuItemClick}
        >
          <MenuItem
            icon={getMenuItemIcon(page.pageTemplate, page.pageIcon)}
            title={page.pageName}
            to={page.slug === 'home' ? '/' : `/${page.slug}`}
            subPages={page.subPages}
            onSubItemClick={handleMenuItemClick}
            redirectConfig={page.redirectConfig}
          />
        </div>
      );
    });
  };

  return (
    <StyledPortalHeader
      data-testid="PortalTopbar-Container"
      header={initialBoot?.theme?.header}
      customCss={headerCss}
      buttonColor={design!.buttonColor}
    >
      <TopBarIndicator isVisible={shouldShowLoadingIndicator} />
      <div
        data-testid="PortalSidebar-MobileContainer"
        data-page-section={EPageSection.MENU}
        className="fixed left-0 top-0 z-50 h-screen w-full transition duration-300 ease-in-out md:hidden"
        style={{
          transform: isOpen ? 'translateX(0)' : 'translateX(-100%)',
          background: `linear-gradient(${initialBoot?.theme?.general?.backgroundColor?.gradientDirection}, ${initialBoot?.theme?.general?.backgroundColor?.colorOne}, ${initialBoot?.theme?.general?.backgroundColor?.colorTwo})`,
        }}
      >
        <StyledMenuScope
          {...menuScopeProps}
          className="menu-container flex h-screen flex-col overflow-y-scroll p-6"
          style={{ height: '100vh' }}
        >
          <div
            className="menu-header flex items-center justify-between"
            data-testid="PortalHeader-UserInfo"
          >
            <button
              data-testid="PortalSidebar-MobileCloseBtn"
              className="menu-close-button ml-auto shrink-0 p-2"
              onClick={() => {
                setIsOpen(!isOpen);
              }}
            >
              {menuDesign?.customMenuCloseIcon ? (
                <CmsImage
                  data-testid="PortalSidebar-MobileCloseIcon"
                  src={menuDesign.customMenuCloseIcon}
                  alt="close menu"
                  width={32}
                  height={32}
                  className="size-8 shrink-0 object-contain"
                />
              ) : (
                <X className="size-8 shrink-0" />
              )}
            </button>
          </div>
          <div data-testid="PortalSidebar-Menu" className="menu-items mt-6 flex flex-col gap-3">
            {isPagesLoading ? (
              <div
                className="flex size-full items-center justify-center"
                data-testid="PortalSidebar-Loading"
              >
                <AcCircularLoader animationUrl={getCustomLoaderAnimation(initialBoot?.theme?.general?.loaderAnimation)} />
              </div>
            ) : (
              getMobileMenuItems()
            )}
          </div>
        </StyledMenuScope>
      </div>
      <div
        data-testid="PortalHeader-Container"
        className={`portal-header header-container mx-[18px] mt-[18px] rounded-xl md:mx-0 md:mt-0`}
      >
        <div className="flex min-h-[56px] items-center gap-4 px-4 py-3 md:px-3 md:py-2">
          <div className="flex min-w-0 flex-1 items-center">
            {initialBoot?.theme?.header?.logo && (
              <CmsImage
                data-testid="PortalHeader-Logo"
                src={initialBoot.theme.header.logo}
                // Height-driven layout (.header-logo sets height, width auto); the
                // resized candidates keep the aspect ratio so the width is unchanged.
                width={0}
                height={0}
                sizes="256px"
                priority
                className="header-logo shrink-0 cursor-pointer object-cover md:h-[60px]"
                alt="logo"
                onClick={() => {
                  window.location.href = preserveQueryParams('/');
                }}
              />
            )}
            {isTopFixedHeader && (
              <StyledMenuScope
                {...menuScopeProps}
                className="menu-items scrollbar-hide hidden min-w-0 items-center gap-8 self-stretch overflow-x-auto [scrollbar-width:none] md:flex"
              >
                {groupedPages.map((page, index) => (
                  <TopbarMenuItem
                    dataPriority={page.priority}
                    dataTestId={`PortalSidebar-Item-${page.slug}`}
                    title={page.pageName}
                    to={page.slug === 'home' ? '/' : `/${page.slug}`}
                    subPages={page.subPages}
                    key={index}
                    isTopFixedHeader={isTopFixedHeader}
                    redirectConfig={page.redirectConfig}
                  />
                ))}
              </StyledMenuScope>
            )}
          </div>
          <div className="ml-auto flex items-center">
            {!isPagesLoading &&
              (isLoggedIn ? (
                <div className="flex items-center">
                  <Dialog open={isLogoutOpen} onOpenChange={setIsLogoutOpen}>
                    <DialogOverlay className="bg-transparent backdrop-blur-sm" />
                    <DialogTrigger asChild>
                      <div
                        className="flex items-center gap-3"
                        data-testid="PortalHeader-UserInfo-Trigger"
                      >
                        <div
                          className="truncate text-right"
                          data-testid="PortalHeader-UserInfo-Name"
                        >
                          {shouldShowPlayerName && (
                            <div className="player-name truncate text-xs font-semibold">
                              {playerData?.playerName || ''}
                            </div>
                          )}
                          {shouldShowPlayerId && playerData?.playerId && (
                            <div className="player-id truncate text-xs font-semibold">
                              ID:{playerData.playerId}
                            </div>
                          )}
                          {((balances.length > 0 && shouldShowBalances) || playerLevelImg) && (
                            <div className="flex items-center justify-end">
                              {shouldShowBalances &&
                                balances.map(({ formattedQuantity, image }, index) => {
                                  // Dynamic max-width based on number of balance items
                                  const maxWidthClass =
                                    balances.length === 1 ? 'max-w-[230px]' : 'max-w-[80px]';

                                  return (
                                    <div
                                      className={`balance-container flex ${maxWidthClass} items-center justify-end gap-x-[2px] pl-1.5 text-xs font-semibold sm:max-w-full md:text-sm`}
                                      key={index}
                                    >
                                      {image && (
                                        <img
                                          src={image}
                                          alt="balance"
                                          className="balance-image size-5"
                                        />
                                      )}
                                      {formattedQuantity && (
                                        <span className="balance-quantity truncate">
                                          {formattedQuantity}
                                        </span>
                                      )}
                                    </div>
                                  );
                                })}
                              {playerLevelImg && (
                                <div
                                  className="flex items-center gap-x-[2px] pl-1.5"
                                  data-testid="PortalHeader-PlayerLevel"
                                >
                                  <img
                                    src={playerLevelImg}
                                    alt="player level"
                                    className="h-6 w-auto md:h-7"
                                  />
                                  {playerLevelText && (
                                    <span
                                      className="truncate text-xs font-semibold md:text-sm"
                                      style={{
                                        color: initialBoot?.theme?.header?.textColor || '#FFFFFF',
                                      }}
                                    >
                                      {playerLevelText}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="avatar-container relative flex items-center justify-center">
                          {playerFrameImg && (
                            <img
                              src={playerFrameImg}
                              alt="player frame"
                              className="avatar-frame absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
                            />
                          )}
                          <img
                            src={playerImg}
                            alt="player"
                            className="avatar size-[34px] rounded-full hover:cursor-pointer md:size-12"
                            data-testid="PortalHeader-UserInfo-Image"
                          />
                        </div>
                      </div>
                    </DialogTrigger>
                    <LogoutModal />
                  </Dialog>
                </div>
              ) : (
                !initialBoot?.settings?.hideAllLoginMethods && (
                  <CustomButton
                    className="login-button ml-auto flex items-center space-x-4 text-white md:flex"
                    onClick={onLoginButtonClick}
                    shouldUseStyle={false}
                  >
                    {t('portal.topbar.loginButton')}
                  </CustomButton>
                )
              ))}
            <button
              data-testid="PortalHeader-MobileMenuBtn"
              className="ml-3 shrink-0 text-white md:hidden"
              style={{ color: menuDesign?.textColor ?? DEFAULT_TEXT_COLOR }}
              onClick={() => setIsOpen(true)}
            >
              {menuDesign?.customMenuIcon ? (
                <CmsImage
                  data-testid="PortalHeader-MobileMenuIcon"
                  src={menuDesign.customMenuIcon}
                  alt="open menu"
                  width={28}
                  height={28}
                  className="menu-button size-7 shrink-0 object-contain"
                />
              ) : (
                <MenuIcon className="menu-button size-7 shrink-0" />
              )}
            </button>
          </div>
        </div>
      </div>
    </StyledPortalHeader>
  );
};
